import type { Customer, FooterLink } from '@/types';

/**
 * Represents the structure of a Customer received from the backend API.
 * This interface should match the backend's customer object as closely as possible.
 */
export interface CustomerDTO {
  name: string;
  address: string;
  address2: string;
  alias: string;
  cashflow: number;
  city: string;
  contact: string;
  cookie_warning: number;
  cookie_warning_link: string | null;
  cookie_warning_text: string | null;
  country: string;
  custom_NG_footer_description: string;
  custom_NG_footer_link: string;
  custom_colour: string | null;
  custom_css: string | null;
  custom_newsletter: string | null;
  custom_tracking_allpages: string | null;
  custom_tracking_receipt: string | null;
  default_tz_name: string;
  email: string;
  events_shown_at_load: number;
  events_shown_at_more: number;
  external_ticketsale: number;
  favicon: string | null;
  fb_organizer_app_id: string;
  fb_pixel_events: number;
  fb_pixel_events_id: string | null;
  fb_ticketsale_app_id: string;
  footer_links?: FooterLink[];
  force_customer_info_button: number;
  frontpage_layout_name: string;
  frontpage_show_time: number;
  frontpage_type: number;
  giftcards: number;
  google_analytics_trackingid_ng: string | null;
  google_tag_manager_trackingid: string | null;
  hide_calendar?: number | boolean;
  hide_categories?: number | boolean;
  hide_categories_everywhere?: number | boolean;
  hide_doorsale_customer_info_button: number;
  hide_search?: number | boolean;
  hide_shop_more_button_in_checkout?: number | boolean;
  hide_venue_on_event_details?: number | boolean;
  hide_venues?: number | boolean;
  invoice_payment: number;
  localization: number;
  login_link: number;
  logo: string | null;
  logoFooter: string | null;
  newsletter: number;
  primary_language: string;
  promoted_in_categories: string[];
  provider_info_address: string | null;
  provider_info_city: string | null;
  provider_info_name: string | null;
  provider_registered: string | null;
  saleend_countdown: boolean;
  show_remaining_tickets: boolean;
  single_sign_on_required: boolean;
  single_sign_on_active?: number; // older cached JSON revisions do not include the sso fields
  single_sign_on_status_url?: string | null;
  single_sign_on_member_usertype?: string | null;
  special_categories: number;
  special_category_name: string;
  sso_price_warning: number;
  structured_data_description_override: string | null;
  structured_data_name_override: string | null;
  telephone: string;
  ticketbuyer_can_cancel_tickets: number;
  vat: string;
  vouchercode_count_transfer: number;
  website: string;
  zip: string;
  psp?: string | null;
  newsletter_localization_active: number;
  terms_localization_active: number;
  extra_terms_localization_active: number;
  localizations: Record<string, {
    newsletter_label: string;
    newsletter_text: string;
    terms_label: string;
    terms_text: string;
    extra_terms_label: string;
    extra_terms_text: string;
    remarks_text: string;
    sso_price_label?: string;
    regular_price_label?: string;
    sso_welcome_label?: string;
    sso_exclusive_message_postfix?: string;
    sso_login_prompt_text?: string;
    sso_no_access_button_text?: string;
    custom_frontpage_html?: string;
  }>;
}

/**
 * Transforms a CustomerDTO object into a Customer object used in the frontend.
 * @param dto - The CustomerDTO object received from the backend.
 * @returns The transformed Customer object.
 */
export function mapDtoToCustomer(dto: CustomerDTO): Customer {
  return {
    ...dto,
    // Add any type conversions or default values if needed
    footer_links: dto.footer_links ?? [], // older cached JSON revisions may not include this field
    show_remaining_tickets: Boolean(dto.show_remaining_tickets),
    saleend_countdown: Boolean(dto.saleend_countdown),
    single_sign_on_required: Boolean(dto.single_sign_on_required),
    single_sign_on_active: Boolean(Number(dto.single_sign_on_active ?? 0)),
    single_sign_on_status_url: dto.single_sign_on_status_url ?? null,
    single_sign_on_member_usertype: dto.single_sign_on_member_usertype ?? null,
    ticketbuyer_can_cancel_tickets: Boolean(dto.ticketbuyer_can_cancel_tickets),
    cookie_warning: Number(dto.cookie_warning),
    newsletter: Number(dto.newsletter),
    giftcards: Number(dto.giftcards),
    external_ticketsale: Number(dto.external_ticketsale),
    invoice_payment: Number(dto.invoice_payment),
    hide_doorsale_customer_info_button: Number(dto.hide_doorsale_customer_info_button),
    force_customer_info_button: Number(dto.force_customer_info_button),
    fb_pixel_events: Number(dto.fb_pixel_events),
    special_categories: Number(dto.special_categories),
    sso_price_warning: Number(dto.sso_price_warning),
    events_shown_at_load: Number(dto.events_shown_at_load),
    events_shown_at_more: Number(dto.events_shown_at_more),
    login_link: Number(dto.login_link),
    localization: Number(dto.localization),
    frontpage_show_time: Boolean(dto.frontpage_show_time),
    newsletter_localization_active: Boolean(dto.newsletter_localization_active),
    terms_localization_active: Boolean(dto.terms_localization_active),
    extra_terms_localization_active: Boolean(dto.extra_terms_localization_active),
    localizations: dto.localizations || {},
    frontpage_type: Number(dto.frontpage_type),
    hide_categories: Boolean(dto.hide_categories),
    hide_categories_everywhere: Boolean(dto.hide_categories_everywhere),
    hide_venues: Boolean(dto.hide_venues),
    hide_calendar: Boolean(dto.hide_calendar),
    hide_search: Boolean(dto.hide_search),
    hide_shop_more_button_in_checkout: Boolean(dto.hide_shop_more_button_in_checkout),
    hide_venue_on_event_details: Boolean(dto.hide_venue_on_event_details),
  };
}
